//ListProductComponent.jsx
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductsBy, fetchCountBy } from "store/slices/productsSlice";

import api from "api/api";
import endpoint from "api/endpoints";
import {
  Breadcrumb,
  ErrorMessage,
  ProductsSkeleton,
  Pagination,
  ProductToolbar,
} from "common";
import useSupersedingRequest from "hooks/useSupersedingRequest";
import useUrlState from "hooks/useUrlState";
import { decodeFilterList, encodeFilterList } from "utils/filterUtils";

import { FilterSidebar, ListProduct } from "..";
import MobileFilterPanel from "../filters/MobileFilterPanel";

export default function ListProductComponent({
  type,
  id,
  breadcrumbPath,
  headerTitle,
}) {
  // The listing's whole state lives in the URL — page, view, sort, perPage and every
  // filter — so any filtered listing can be linked, bookmarked, and walked with
  // Back/Forward. In-app navigation to another listing (a category row, a nav link) is
  // a fresh path with no params, which is what used to need reset effects.
  const [
    {
      page: rawPage,
      view: rawView,
      sort: rawSort,
      perPage: rawPerPage,
      minPrice: rawMinPrice,
      maxPrice: rawMaxPrice,
      categories: categoriesParam,
      brands: brandsParam,
      manufacturers: manufacturersParam,
      search: searchParam,
    },
    setUrlState,
  ] = useUrlState({
    page: 1,
    view: "grid",
    sort: "",
    perPage: 12,
    minPrice: "",
    maxPrice: "",
    categories: "",
    brands: "",
    manufacturers: "",
    search: "",
  });
  // Only a positive integer is a page. Anything else — `-1`, `-1.2`, `2.2`, `0` — is not
  // a near-miss to be salvaged but a malformed URL, so it falls back to 1 rather than
  // being rounded into a page the user never asked for. The hook has already turned
  // non-numeric and non-finite values into 1.
  //
  // This runs on read, before any fetch, so a malformed page never reaches the offset
  // maths. Only the upper bound has to wait for the count; see further down.
  //
  // The same narrow-at-the-call-site rule covers every URL-sourced value: these reach
  // SQL fragments and the sort whitelist, and a pasted `?minPrice=abc` must degrade to
  // "no filter", not to `ip.price >= NaN`.
  const page = Number.isInteger(rawPage) && rawPage > 0 ? rawPage : 1;
  const view = rawView === "list" ? "list" : "grid";
  const sort = rawSort === "asc" || rawSort === "desc" ? rawSort : "";
  const perPage = [12, 24, 48].includes(rawPerPage) ? rawPerPage : 12;
  const cleanPrice = (value) =>
    value !== "" && Number.isFinite(Number(value)) ? value : "";
  const setPage = (next) => setUrlState({ page: next });
  const setView = (next) => setUrlState({ view: next });
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const filters = useMemo(
    () => ({
      minPrice: cleanPrice(rawMinPrice),
      maxPrice: cleanPrice(rawMaxPrice),
      selectedCategories: decodeFilterList(categoriesParam),
      selectedBrands: decodeFilterList(brandsParam),
      selectedManufacturers: decodeFilterList(manufacturersParam),
      searchByName: searchParam,
    }),
    [
      rawMinPrice,
      rawMaxPrice,
      categoriesParam,
      brandsParam,
      manufacturersParam,
      searchParam,
    ]
  );
  const [listBrands, setListBrands] = useState([]);
  const [listCategories, setListCategories] = useState([]);

  // The Category facet's tree — the reference's single-select navigation rail (see
  // `CategoryFacet`). Derived from the classification store `Header` already fills on
  // every route — no request, and the same `child.length > 0` filter as the navbar menu
  // and `/categories`, so every surface shows the same 22 categories.
  //
  // It renders on the unscoped listings *and* on class pages — a class page is exactly
  // where the facet shows your position (the active category expands its subcategories
  // in place). Brand listings keep their flat, API-scoped `listCategories` instead.
  const classificationClasses = useSelector(
    (state) => state.classification?.classes
  );
  const categoryTree = useMemo(() => {
    if (type !== "all" && type !== "name" && type !== "classification")
      return [];

    return (classificationClasses || [])
      .filter(
        (category) => Array.isArray(category.child) && category.child.length > 0
      )
      .map((category) => ({
        id: category.id,
        name: category.name,
        children: category.child.map((sub) => ({
          id: sub.id,
          name: sub.name,
        })),
      }));
  }, [classificationClasses, type]);
  const activeCategoryId = type === "classification" ? id : null;
  const [listManufacturers, setListManufacturers] = useState([]);

  // Fetch brands, categories, and manufacturers based on type and id
  useEffect(() => {
    const fetchRelatedData = async () => {
      try {
        if (type === "classification" && id) {
          const [brandsRes, manufacturersRes] = await Promise.all([
            api.get(endpoint.GET_BRANDS_BY_CLASSIFICATION(id)),
            api.get(endpoint.GET_ALL_UNIQUE_MANUFACTURERS, {
              params: { classId: id },
            }),
          ]);
          setListBrands(brandsRes.data || []);
          setListCategories([]);
          setListManufacturers(manufacturersRes.data || []);
        } else if (type === "brand" && id) {
          const [categoriesRes, manufacturersRes] = await Promise.all([
            api.get(endpoint.GET_CLASSIFICATIONS_BY_BRAND(id)),
            api.get(endpoint.GET_ALL_UNIQUE_MANUFACTURERS),
          ]);
          setListCategories(categoriesRes.data || []);
          setListBrands([]);
          setListManufacturers(manufacturersRes.data || []);
        } else if (type === "promotion") {
          const [brandsRes, manufacturersRes] = await Promise.all([
            api.get(endpoint.GET_UNIQUE_PROMOTION_BRANDS()),
            api.get(endpoint.GET_UNIQUE_PROMOTION_MANUFACTURERS()),
          ]);
          setListBrands(brandsRes.data || []);
          setListCategories([]);
          setListManufacturers(manufacturersRes.data || []);
        } else if (type === "all" || type === "name") {
          const searchTerm = type === "name" ? id : null;
          const [brandsRes, manufacturersRes] = await Promise.all([
            api.get(endpoint.GET_ALL_UNIQUE_BRANDS, {
              params: searchTerm ? { searchByName: searchTerm } : {},
            }),
            api.get(endpoint.GET_ALL_UNIQUE_MANUFACTURERS, {
              params: searchTerm ? { searchByName: searchTerm } : {},
            }),
          ]);
          setListBrands(brandsRes.data || []);
          setListCategories([]);
          setListManufacturers(manufacturersRes.data || []);
        } else {
          setListBrands([]);
          setListCategories([]);
          setListManufacturers([]);
        }
      } catch (error) {
        setListBrands([]);
        setListCategories([]);
        setListManufacturers([]);
      }
    };

    fetchRelatedData();
  }, [type, id]);

  const dispatch = useDispatch();

  const priceKey = `${filters.minPrice || ""}_${filters.maxPrice || ""}`;
  const searchKey = filters.searchByName
    ? encodeURIComponent(filters.searchByName)
    : "";
  const categoriesKey =
    Array.isArray(filters.selectedCategories) &&
    filters.selectedCategories.length > 0
      ? filters.selectedCategories.join("%")
      : "";
  const brandsKey =
    Array.isArray(filters.selectedBrands) && filters.selectedBrands.length > 0
      ? filters.selectedBrands.join("%")
      : "";
  const manufacturersKey =
    Array.isArray(filters.selectedManufacturers) &&
    filters.selectedManufacturers.length > 0
      ? filters.selectedManufacturers.join("%")
      : "";
  const effectiveId = id || "all";
  const key = `${effectiveId}_${perPage}_${sort}_${priceKey}_${searchKey}_${categoriesKey}_${brandsKey}_${manufacturersKey}_${page}`;
  const countKey = `${effectiveId}_${priceKey}_${searchKey}_${categoriesKey}_${brandsKey}_${manufacturersKey}`;

  const products = useSelector(
    (state) => state.products.productsByPage[key] || []
  );
  const isLoading = useSelector((state) => state.products.isLoading);
  const error = useSelector((state) => state.products.error);
  // ③ `countKey` embeds every filter value, so applying a filter always produces a key
  // the store has never seen — undefined until the count request lands. Resolving that
  // to 0 made the toolbar flash "Showing 0 of 0 products" and, worse, drove `totalPages`
  // to 1, which unmounted the pager (see its gate below) and took ~94px out of the
  // document mid-interaction. Measured: docH at the skeleton stage is 1914 with this
  // held, 1820 without.
  //
  // So hold the last count we actually knew. It is stale for the second or two the new
  // count is in flight, which is exactly what `ProductToolbar`'s `loading` branch already
  // assumes — it derives the shown range from `page`/`perPage`/`total`.
  //
  // Adjusting state during render rather than in an effect: the sanctioned
  // "derive from a changed input" pattern, and it re-renders before commit, so no
  // intermediate 0 ever reaches the DOM. An effect would paint the 0 first.
  const countedTotal = useSelector(
    (state) => state.products.totalCounts?.[countKey]
  );
  const [lastKnownTotal, setLastKnownTotal] = useState(0);
  if (countedTotal !== undefined && countedTotal !== lastKnownTotal) {
    setLastKnownTotal(countedTotal);
  }
  const total = countedTotal !== undefined ? countedTotal : lastKnownTotal;

  // Cache check selectors
  const initialCountKey = `${effectiveId}_`;
  const hasInitialCount = useSelector(
    (state) => state.products.totalCounts?.[initialCountKey] !== undefined
  );
  const hasFilteredCount = useSelector(
    (state) => state.products.totalCounts?.[countKey] !== undefined
  );
  const hasProducts = useSelector(
    (state) => state.products.productsByPage[key] !== undefined
  );

  // Pagination stays clickable during a fetch, so the user can supersede a slow
  // page before it lands. Keyed on the products cache key so a re-run that isn't
  // actually a new target — a count landing, say — neither aborts nor duplicates
  // the request in flight. Only the products fetch goes through this; the count
  // fetches are page-independent and must not be cancelled by a page change.
  const requestProducts = useSupersedingRequest();

  // No reset effects for sort or filters: they live in the URL now, and navigating to
  // another listing is a new URL with no params. An effect here would also wipe state
  // restored by a Back navigation, since it runs on mount too — the reasoning that
  // already applied to `page`.

  // Combined fetch: count + products in parallel using Promise.all
  useEffect(() => {
    if (!id && type !== "all") return;

    const resolvedId = effectiveId === "all" ? null : effectiveId;
    const hasActiveFilters =
      filters.minPrice ||
      filters.maxPrice ||
      filters.searchByName ||
      (Array.isArray(filters.selectedCategories) &&
        filters.selectedCategories.length > 0) ||
      (Array.isArray(filters.selectedBrands) &&
        filters.selectedBrands.length > 0) ||
      (Array.isArray(filters.selectedManufacturers) &&
        filters.selectedManufacturers.length > 0);

    const promises = [];

    // Initial count (no filters)
    if (!hasInitialCount) {
      promises.push(dispatch(fetchCountBy({ type, id: resolvedId })));
    }

    // Filtered count (only when filters are active)
    if (!hasFilteredCount && hasActiveFilters) {
      promises.push(
        dispatch(
          fetchCountBy({
            type,
            id: resolvedId,
            minPrice: filters.minPrice,
            maxPrice: filters.maxPrice,
            searchByName: filters.searchByName,
            selectedCategories: filters.selectedCategories,
            selectedBrands: filters.selectedBrands,
            selectedManufacturers: filters.selectedManufacturers,
          })
        )
      );
    }

    // Products. A page the user has already navigated past is wasted NetSuite work,
    // and letting it settle would fight the newer request over the slice's single
    // isLoading flag — requestProducts aborts it, but only when the key changed.
    if (!hasProducts) {
      promises.push(
        requestProducts(key, () =>
          dispatch(
            fetchProductsBy({
              type,
              id: resolvedId,
              page,
              limit: perPage,
              sort,
              minPrice: filters.minPrice,
              maxPrice: filters.maxPrice,
              searchByName: filters.searchByName,
              selectedCategories: Array.isArray(filters.selectedCategories)
                ? filters.selectedCategories
                : [],
              selectedBrands: Array.isArray(filters.selectedBrands)
                ? filters.selectedBrands
                : [],
              selectedManufacturers: Array.isArray(
                filters.selectedManufacturers
              )
                ? filters.selectedManufacturers
                : [],
            })
          )
        )
      );
    }

    if (promises.length > 0) {
      Promise.all(promises).catch(() => {
        // Individual thunks handle their own errors via rejectWithValue
      });
    }
  }, [
    dispatch,
    requestProducts,
    key,
    type,
    id,
    effectiveId,
    perPage,
    sort,
    page,
    hasInitialCount,
    hasFilteredCount,
    hasProducts,
    filters.minPrice,
    filters.maxPrice,
    filters.searchByName,
    filters.selectedCategories,
    filters.selectedBrands,
    filters.selectedManufacturers,
  ]);
  const totalPages = Math.ceil(total / perPage) || 1;

  // A URL can name a page that doesn't exist (`?page=234234`), so once the real count
  // is in, land the user on the last page instead. `total > 0` matters: it's 0 until the
  // count arrives, and treating that as "one page" would bounce a perfectly good deep
  // link to page 50 on the very first render.
  //
  // Nothing here guards the *count* fetch — that was the mistake the first time. The
  // count is what makes `total` non-zero, so gating it on `total` deadlocks and the page
  // renders "No products found" forever. The doomed products request for the bad page
  // does still go out once; the backend answers it as an empty page rather than a 500,
  // and `productsByPage[key]` becomes `[]`, so `hasProducts` stops it repeating.
  //
  // Replace, not push, or Back returns to the bad page and bounces off the correction.
  // No dep array: `setUrlState` derives from `useSearchParams`, whose setter changes
  // identity on every URL change, so listing it would re-run this on every page change.
  const pageOutOfRange = total > 0 && page > totalPages;
  // One correction for every bad value, upper and lower: `safePage` is what the listing
  // will actually show, so writing it back covers `-1` → 1, `1.5` → 1 and `234234` → 925
  // in a single condition. `view` gets the same treatment for `?view=banana`.
  const safePage = pageOutOfRange ? totalPages : page;
  useEffect(() => {
    const patch = {};
    if (safePage !== rawPage) patch.page = safePage;
    if (view !== rawView) patch.view = view;
    // Same treatment for the rest of the URL-sourced values: reading already narrowed
    // them, so a difference here means the param carries junk (`?sort=DROP`,
    // `?perPage=999`, `?minPrice=abc`). Left alone it would ride along in every link
    // copied from the address bar; written back (replace, not push — it's a
    // correction, not a step) the URL heals to what the page is actually showing.
    if (sort !== rawSort) patch.sort = sort;
    if (perPage !== rawPerPage) patch.perPage = perPage;
    if (filters.minPrice !== rawMinPrice) patch.minPrice = filters.minPrice;
    if (filters.maxPrice !== rawMaxPrice) patch.maxPrice = filters.maxPrice;
    if (Object.keys(patch).length > 0) setUrlState(patch, { replace: true });
  });

  // ② The skeleton has to occupy the space the grid just vacated, or the document
  // shrinks under the reader and the browser clamps `scrollY` to the bottom of the
  // shorter page. **Measured, not derived from the card's geometry** — that contract
  // drifted twice (the placeholder ran 142px, then ~160px, shorter per card than the
  // real card as the card kept growing), and each time the difference reappeared as a
  // ~500px void between the skeleton's last row and the pager, because the container
  // reservation held the REAL height while the skeleton inside stayed short.
  //
  // So both numbers are measured off the live DOM while the real results are on
  // screen, applied while they are not: the container's height reserves the page
  // height (anti scroll-jump), and one real card's height is fed to every skeleton
  // card (`cardMinHeight`) so the skeleton fills that reservation instead of
  // undershooting it. Self-healing: when the card next grows, both measurements grow
  // with it. `view` is a dependency because grid and list rows are nothing like the
  // same height. A layout effect, not `useEffect`, so the measurement happens before
  // paint. Known residual: changing perPage 48→12 reserves 48 cards' height for a
  // 12-card skeleton — inherent to reserving the OLD height, rare, and transient.
  const resultsRef = useRef(null);
  const [reservedHeight, setReservedHeight] = useState(0);
  const [reservedCardHeight, setReservedCardHeight] = useState(0);
  useLayoutEffect(() => {
    const el = resultsRef.current;
    if (el) {
      setReservedHeight(el.offsetHeight);
      const card = el.querySelector("article");
      if (card) setReservedCardHeight(card.offsetHeight);
    }
  }, [products, view]);

  // One URL patch per apply: every filter key plus the page reset, so a filter change
  // is a single history entry and Back undoes it atomically. The sidebar's 200ms
  // debounce has already collapsed rapid edits before this fires.
  const handleApplyFilters = (newFilters) => {
    setUrlState({
      minPrice: newFilters?.minPrice ?? "",
      maxPrice: newFilters?.maxPrice ?? "",
      categories: encodeFilterList(newFilters?.selectedCategories),
      brands: encodeFilterList(newFilters?.selectedBrands),
      manufacturers: encodeFilterList(newFilters?.selectedManufacturers),
      search: newFilters?.searchByName ?? "",
      page: 1,
    });
  };

  return (
    // Reference listing screen — Dens N Dente Tailwind.dc.html:566-573. Container is
    // max-w-container (1280px, down from max-w-screen-2xl's 1536px) with --gutter
    // padding, and the two columns are a fixed 268px track plus the rest.
    // data-shell-wide is what widens max-w-container to --container-3xl, via the
    // `:has()` rule in keyframes.js. The listing is browse-dense: more columns help
    // scanning, where a reading page would suffer from the long measure.
    <div
      data-shell-wide="1"
      className="mx-auto max-w-container px-[var(--gutter)] pb-[var(--space-20)] pt-[var(--space-6)]"
    >
      <Breadcrumb path={breadcrumbPath} />

      {/* The reference dropped the visible page title — the breadcrumb names the
          category and the toolbar carries the count, so the heading was buying nothing
          but vertical space. Kept as `sr-only` rather than deleted: removing the only
          h1 leaves screen-reader users with no heading to navigate by, and the
          reference is a prototype where that doesn't bite. */}
      <h1 className="sr-only">
        {headerTitle}
        {total > 0 ? ` — ${total.toLocaleString()} products` : ""}
      </h1>

      {/* The reference has no mobile treatment for this screen, so grid-cols-1 below
          lg is a deliberate addition — the 236px track would overflow a phone. */}
      <div className="mt-[var(--space-4)] grid grid-cols-1 items-start gap-[var(--space-7)] lg:grid-cols-[236px_minmax(0,1fr)]">
        {/* Filters (desktop). The layout lives on the aside, not on FilterSidebar's
            className, matching the reference: the aside is the sticky self-scrolling
            column and the filter panel is a card inside it. `flex-col gap-[var(--space-5)]`
            is there for the same reason it is upstream — the column is built to hold
            several stacked cards, even though ours is currently one.

            `top-[175px]` and the matching `calc(100vh - 195px)` are the reference's own
            numbers, kept literally — and they happen to match this app's measured
            header height (159px + breathing room). The own-scroll is the point — a
            long filter list can no longer push the page taller than the viewport. */}

        <aside className="dnd-thinscroll hidden lg:sticky lg:top-[175px] lg:flex lg:max-h-[calc(100vh-195px)] lg:flex-col lg:gap-[var(--space-5)] lg:overflow-y-auto lg:pr-[2px]">
          <FilterSidebar
            filters={filters}
            onApplyFilters={handleApplyFilters}
            listBrands={listBrands}
            listCategories={listCategories}
            categoryTree={categoryTree}
            activeCategoryId={activeCategoryId}
            listManufacturers={listManufacturers}
          />
        </aside>

        {/* Main area. min-w-0 because a grid child defaults to min-content, which a
            long product name would otherwise blow past. */}
        <div className="min-w-0">
          {/* Mobile filters */}

          <MobileFilterPanel
            open={showMobileFilters}
            onToggle={() => setShowMobileFilters((open) => !open)}
          >
            <FilterSidebar
              filters={filters}
              onApplyFilters={handleApplyFilters}
              listBrands={listBrands}
              listCategories={listCategories}
              categoryTree={categoryTree}
              activeCategoryId={activeCategoryId}
              listManufacturers={listManufacturers}
            />
          </MobileFilterPanel>

          {/* Toolbar with Grid/List toggle */}

          <ProductToolbar
            page={page}
            shown={products.length}
            perPageOptions={[12, 24, 48]}
            onPerPageChange={(e) =>
              setUrlState({ perPage: Number(e.target.value), page: 1 })
            }
            perPage={perPage}
            sort={sort}
            onSortChange={(e) => setUrlState({ sort: e.target.value })}
            total={total}
            view={view}
            onViewChange={setView}
            // Same window as the skeleton gate below: a just-changed cache key has no
            // data and no in-flight flag yet, and the toolbar's non-loading branch reads
            // `shown` (= 0), so the range flickers to "Showing 0 of …" for ~35ms. Its
            // loading branch derives the range from page/perPage/total instead, which is
            // right for both cases.
            loading={isLoading || !hasProducts}
          />

          {/* Shimmer stand-in for the incoming page, matching the active view mode
              (BEHAVIOR.md). The products cache is keyed, so `products` is already []
              here — nothing is being hidden.

              **Also gated on `!hasProducts`, and that is the whole fix for the filter
              jump.** `isLoading` and "this key has data" are two different questions,
              and there is a window where the answer to both is misleading: applying a
              filter changes the cache *key*, so `products` becomes [] immediately,
              while `isLoading` is still false because the fetch is dispatched from an
              effect — i.e. after this commit. Measured at ~35-50ms.
              Without this, that window renders the *empty state* instead: the results
              column drops 1547px → 88px, and the browser clamps `scrollY` to
              `scrollHeight - innerHeight`, which pins the viewport to the bottom of
              the shortened page. Gating only the empty state is worse still — nothing
              would render at all.

              `!error` matters too: `hasProducts` stays false after a failed fetch, so
              without it the skeleton would shimmer forever next to the error. */}
          {(isLoading || !hasProducts) && !error && (
            <div
              style={reservedHeight ? { minHeight: reservedHeight } : undefined}
            >
              <ProductsSkeleton
                view={view}
                count={perPage}
                cardMinHeight={
                  view === "grid" && reservedCardHeight
                    ? reservedCardHeight
                    : undefined
                }
              />
            </div>
          )}
          {error && <ErrorMessage message={error} />}

          {/* `hasProducts` is `productsByPage[key] !== undefined` — "this key has been
              fetched and stored", which is the difference between *no results* and
              *not asked yet*. Reading the second as the first is what caused the jump. */}
          {!isLoading && !error && hasProducts && products.length === 0 && (
            <div className="text-gray-500 py-8 text-center">
              No products found for this category.
            </div>
          )}

          {/* Actual products: switch Grid/List */}
          {!isLoading &&
            !error &&
            products.length > 0 &&
            (view === "grid" ? (
              <div
                ref={resultsRef}
                className="pc-grid [animation:dndRevealUp_.38s_var(--ease-out)_both]"
              >
                {products.map((product) => (
                  <ListProduct
                    key={product.id}
                    product={product}
                    listType="grid"
                  />
                ))}
              </div>
            ) : (
              <div
                ref={resultsRef}
                className="flex flex-col gap-[10px] [animation:dndRevealUp_.38s_var(--ease-out)_both]"
              >
                <div className="contents">
                  {products.map((product) => (
                    <ListProduct
                      key={product.id}
                      product={product}
                      listType="list"
                    />
                  ))}
                </div>
              </div>
            ))}

          {/* Bottom pagination — gated on totalPages, not products.length, so a
              page change dims it (see its `loading` prop) instead of unmounting
              it and collapsing the page height. */}
          {!error && totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                loading={isLoading}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
